import axios from 'axios';
import { getApiBaseUrl } from './api';
import { getToken } from './auth';
import type { PaginatedResponse } from './marketplaceApi';

const delphiApi = axios.create({
  baseURL: `${getApiBaseUrl()}/delphi`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include token
delphiApi.interceptors.request.use(
  (config) => {
    config.baseURL = `${getApiBaseUrl()}/delphi`;
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export interface ContentObject {
  id: number;
  name?: string;
  display_name?: string;
  series?: string;
  series_id?: number;
  tournament?: string;
  tournament_id?: number;
  venue?: string;
  stadium?: string;
  start_date?: string;
  match_format?: string;
}

export interface PromptDefaults {
  initial: { key: string | null; source: 'series' | 'default' };
  visual: { key: string | null; source: 'series' | 'random' };
}

export interface GenerationSession {
  id: number;
  content_type: number;
  object_id: number;
  content_type_name: string;
  content_object?: ContentObject | null;
  frozen_context: Record<string, any>;
  status: 'active' | 'completed' | 'archived';
  created_at: string;
  updated_at: string;
  created_by?: number;
  iterations?: Iteration[];
  assets?: SessionAsset[];
  latest_iteration?: {
    id: number;
    version: number;
    created_at: string;
  };
  iteration_count?: number;
  published_article?: number | null;
}

export interface Iteration {
  id: number;
  session: number;
  version: number;
  user_instruction: string;
  text_content: string;
  
  // Structured content fields
  title?: string;
  description?: string;
  summary?: string;
  slug?: string;
  seo_meta_title?: string;
  seo_meta_description?: string;
  seo_meta_keywords?: string[];
  // Prompt for the match banner written with the draft (no real person named); empty when the prompt template did not write one
  banner_prompt?: string;
  
  llm_metadata: {
    model?: string;
    tier?: string;
    provider?: string;
    structured?: boolean;
    tokens?: {
      prompt_tokens?: number;
      completion_tokens?: number;
      total_tokens?: number;
    };
  };
  created_at: string;
  created_by?: number;
}

export interface SessionAsset {
  id: number;
  session: number;
  source_iteration?: number;
  asset_type: 'SEO_META' | 'SOCIAL_X' | 'SOCIAL_FB' | 'SOCIAL_INSTAGRAM' | 'SOCIAL_TELEGRAM' | 'VISUAL_PROMPT' | 'VISUAL_IMAGE' | 'TRANSLATION_HI';
  content: Record<string, any>;
  
  // Hindi translation fields
  title_hi?: string;
  description_hi?: string;
  summary_hi?: string;
  seo_meta_title_hi?: string;
  seo_meta_description_hi?: string;
  seo_meta_keywords_hi?: string[];
  
  is_selected: boolean;
  created_at: string;
  created_by?: number;
}

export interface InitSessionRequest {
  app_label: string;
  model: string;
  object_id: number;
  prompt_key?: string;
  additional_instruction?: string;
  llm_model?: string;
}

export interface RefineContentRequest {
  instruction: string;
  selection_start?: number;
  prompt_key?: string;
  llm_model?: string;
}

export interface GenerateAssetsRequest {
  asset_types: SessionAsset['asset_type'][];
  iteration_id?: number;
  banner_options?: {
    aspect_ratio?: string;
    resolution?: string;
    // 'static' draws the rules-based banner (no image model); plate then optionally names the picture
    style?: 'ai' | 'static';
    plate?: string;
  };
  prompt_key?: string;
  visual_prompt_key?: string;
  visual_prompt_override?: string;
  // Draw the banner from the banner prompt written with the draft (Iteration.banner_prompt)
  use_suggested_banner_prompt?: boolean;
  llm_model?: string;
  async?: boolean;
}

export interface PublishRequest {
  iteration_id: number;
  asset_ids?: number[];
}

export interface PromptTemplate {
  id: number;
  name: string;
  key: string;
  prompt_type: string;
  system_prompt: string;
  user_prompt: string;
  description?: string;
  is_default: boolean;
  is_active: boolean;
  updated_at: string;
}

export interface GeminiModel {
  id: string;
  label: string;
  family: 'pro' | 'flash';
}

export const delphiService = {
  // Sessions
  listSessions: async (params?: {
    app_label?: string;
    model?: string;
    object_id?: number;
    status?: string;
    page?: number;
    page_size?: number;
  }) => {
    const response = await delphiApi.get<PaginatedResponse<GenerationSession>>('/sessions/', { params });
    return response.data;
  },

  getSession: async (id: number) => {
    const response = await delphiApi.get<GenerationSession>(`/sessions/${id}/`);
    return response.data;
  },

  /** The prompts to preselect for a content object, by the same rule the server uses to draft and draw its banner (series, then tour, then default). */
  getPromptDefaults: async (params: { app_label: string; model: string; object_id: number }) => {
    const response = await delphiApi.get<PromptDefaults>('/prompt-defaults/', { params });
    return response.data;
  },

  initSession: async (data: InitSessionRequest) => {
    const response = await delphiApi.post<GenerationSession>('/sessions/init/', data);
    return response.data;
  },

  refineContent: async (sessionId: number, data: RefineContentRequest) => {
    const response = await delphiApi.post<Iteration>(`/sessions/${sessionId}/refine/`, data);
    return response.data;
  },

  generateAssets: async (sessionId: number, data: GenerateAssetsRequest) => {
    const response = await delphiApi.post<SessionAsset[] | { task_id: string; status: string }>(
      `/sessions/${sessionId}/generate-assets/`,
      data
    );
    return response.data;
  },

  translateToHindi: async (sessionId: number, iterationId?: number, prompt_key?: string, llm_model?: string) => {
    const response = await delphiApi.post<SessionAsset>(
      `/sessions/${sessionId}/translate/`,
      {
        iteration_id: iterationId,
        prompt_key,
        llm_model,
      }
    );
    return response.data;
  },

  publish: async (sessionId: number, data: PublishRequest) => {
    const response = await delphiApi.post<{ message: string; iteration_id: number; session_id: number; article_id?: number; article_title?: string }>(
      `/sessions/${sessionId}/publish/`,
      data
    );
    return response.data;
  },

  // Iterations
  listIterations: async (sessionId?: number) => {
    const params = sessionId ? { session_id: sessionId } : {};
    const response = await delphiApi.get<Iteration[]>('/iterations/', { params });
    return response.data;
  },

  getIteration: async (id: number) => {
    const response = await delphiApi.get<Iteration>(`/iterations/${id}/`);
    return response.data;
  },

  // Assets
  listAssets: async (sessionId?: number) => {
    const params = sessionId ? { session_id: sessionId } : {};
    const response = await delphiApi.get<SessionAsset[]>('/assets/', { params });
    return response.data;
  },

  updateAsset: async (id: number, data: Partial<SessionAsset>) => {
    const response = await delphiApi.patch<SessionAsset>(`/assets/${id}/`, data);
    return response.data;
  },

  deleteAsset: async (id: number) => {
    await delphiApi.delete(`/assets/${id}/`);
  },

  deleteSession: async (id: number) => {
    await delphiApi.delete(`/sessions/${id}/`);
  },

  refreshFrozenContext: async (sessionId: number) => {
    const response = await delphiApi.post<{ message: string; frozen_context: Record<string, any> }>(
      `/sessions/${sessionId}/refresh-context/`
    );
    return response.data;
  },

  regenerateInitialContent: async (sessionId: number, prompt_key?: string, llm_model?: string) => {
    const response = await delphiApi.post<{ message: string; iteration: Iteration }>(
      `/sessions/${sessionId}/regenerate-initial/`,
      { prompt_key, llm_model }
    );
    return response.data;
  },

  // Save a manually-edited version - no LLM call. Omit a field to keep it
  // unchanged from the latest iteration (only send what the editor touched).
  saveManualEdit: async (
    sessionId: number,
    data: { title?: string; slug?: string; description?: string; summary?: string; text_content?: string }
  ) => {
    const response = await delphiApi.post<Iteration>(`/sessions/${sessionId}/edit/`, data);
    return response.data;
  },

  // Gemini models selectable for content generation and lineup AI tools.
  // Small and effectively static, so cache the one call for this session.
  listModels: (() => {
    let cache: Promise<GeminiModel[]> | null = null;
    return async () => {
      if (!cache) {
        cache = delphiApi.get<GeminiModel[]>('/models/').then((r) => r.data);
      }
      return cache;
    };
  })(),

  listPrompts: (() => {
    const cache = new Map<string, Promise<PromptTemplate[]>>();
    return async (params?: { prompt_type?: string; key?: string }) => {
      const key = JSON.stringify(params || {});
      if (cache.has(key)) {
        return cache.get(key)!;
      }
      const promise = delphiApi.get<PromptTemplate[]>('/prompts/', { params }).then((r) => r.data);
      cache.set(key, promise);
      return promise;
    };
  })(),

  getTaskStatus: async (taskId: string) => {
    const response = await delphiApi.get<{ task_id: string; status: string; result?: any }>(
      `/tasks/status/`,
      { params: { task_id: taskId } }
    );
    return response.data;
  },

  getOrCreateSession: async (data: { app_label: string; model: string; object_id: number; prompt_key?: string }) => {
    const response = await delphiApi.post<GenerationSession>('/sessions/get-or-create/', data);
    return response.data;
  },

  sessionsByObject: async (params: { app_label: string; model: string; object_ids: number[] }) => {
    const response = await delphiApi.get<Record<string, number>>('/sessions/by-object/', {
      params: {
        app_label: params.app_label,
        model: params.model,
        object_ids: params.object_ids.join(','),
      },
    });
    return response.data;
  },
};


// The pictures the rules-based banner can use; '' lets the rules choose by match, format and start time.
export const STATIC_BANNER_PLATES: { value: string; label: string }[] = [
  { value: '', label: 'Automatic (by the rules)' },
  { value: 'night_stadium', label: 'Stadium at night' },
  { value: 'day_stadium', label: 'Stadium by day' },
  { value: 'night_wickets', label: 'Wickets at night' },
  { value: 'day_wickets', label: 'Wickets by day' },
  { value: 'trophy_podium', label: 'Trophy on a podium' },
  { value: 'toss_coin', label: 'Toss coin' },
  { value: 'open_ground', label: 'Open ground' },
];
